import axios from 'axios';

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000',
  timeout: 30000, // 30 second timeout
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add a request interceptor to include the auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Multipart uploads: default `Content-Type: application/json` breaks FormData — multer then sees no file (400).
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    try {
      const userRaw = localStorage.getItem('user');
      const activeHospitalId = localStorage.getItem('activeHospitalId');
      if (userRaw && activeHospitalId) {
        const user = JSON.parse(userRaw) as { role?: string; adminScope?: string };
        const injectHospitalId =
          (user.role === 'HOSPITAL_ADMIN' && user.adminScope === 'CHAIN') ||
          user.role === 'CHARTERED_ACCOUNTANT';
        if (injectHospitalId) {
          const url = String(config.url ?? '');
          const isChainWideList =
            url.includes('/api/appointment/get-by-date') ||
            url.includes('/api/appointment/get-upcoming') ||
            url.includes('/api/appointment/search') ||
            url.includes('/api/leads/sidebar/ipd-cases') ||
            url.includes('/api/leads/sidebar/ipd-admitted');
          const existingParams = (config.params as Record<string, unknown>) ?? {};
          const body = config.data as Record<string, unknown> | undefined;
          const bodyHospitalId =
            body?.hospitalId != null ? String(body.hospitalId).trim() : '';
          // Only inject the active hospital fallback when the caller has NOT already
          // supplied an explicit hospitalId (including "all" for consolidated reports).
          if (
            !isChainWideList &&
            !Object.prototype.hasOwnProperty.call(existingParams, 'hospitalId') &&
            !bodyHospitalId
          ) {
            config.params = { ...existingParams, hospitalId: activeHospitalId };
          }
        }
      }
    } catch {
      /* ignore */
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    error.config;

    // Handle unauthorized access
    if (error.response?.status === 401) {
      // Clear authentication data
      localStorage.removeItem('token');
      localStorage.removeItem('user');

      // Only redirect if not already on login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    // Handle server errors
    if (error.response?.status >= 500) {
      console.error('Server error:', error);
    }

    // Handle network errors (e.g. backend not running)
    if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
      const base = error.config?.baseURL || '';
      if (base.includes('localhost') || base.includes('127.0.0.1')) {
        console.error(
          'Network error: Backend API unreachable. Start the backend server (e.g. run "npm run dev" in the backend folder) and ensure it listens on the URL shown above.'
        );
      } else {
        console.error('Network error - please check your connection');
      }
    }

    // Handle timeout
    if (error.code === 'ECONNABORTED') {
      console.error('Request timed out');
    }

    return Promise.reject(error);
  }
);

export { api }; 